import { lazy } from 'react';

// project import
import MainLayout from 'layout/MainLayout';
import CommonLayout from 'layout/CommonLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import Page404 from 'pages/Page404';

// Auth
const AuthLogin = Loadable(lazy(() => import('pages/auth/login')));
const AuthRegister = Loadable(lazy(() => import('pages/auth/register')));
const AuthForgotPassword = Loadable(lazy(() => import('pages/auth/forgot-password')));
const AuthResetPassword = Loadable(lazy(() => import('pages/auth/reset-password')));
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/check-mail')));
const AuthCodeVerification = Loadable(lazy(() => import('pages/auth/code-verification')));

// Tenants
const Customer = Loadable(lazy(() => import('pages/Customer')));
const TenantDetail = Loadable(lazy(() => import('pages/Tenant')));

// Telephony
const DIDManagementPublic = Loadable(lazy(() => import('pages/DIDManagement/Public')));
const SipTrunks = Loadable(lazy(() => import('pages/SipTrunks')));
const InboundDIDs = Loadable(lazy(() => import('pages/InboundDIDs')));
const Domains = Loadable(lazy(() => import('pages/Domains')));

// Reference data
const TemplatePublic = Loadable(lazy(() => import('pages/Template/Public')));
const VoicePublic = Loadable(lazy(() => import('pages/Voice/Public')));
const ActionPublic = Loadable(lazy(() => import('pages/Action/Public')));
const LanguagePublic = Loadable(lazy(() => import('pages/Language/Public')));
const CallTypePublic = Loadable(lazy(() => import('pages/CallType/Public')));
const ReferenceChannels = Loadable(lazy(() => import('pages/ReferenceChannels')));
const WhatsAppTemplates = Loadable(lazy(() => import('pages/WhatsAppTemplates')));
const InstagramTemplates = Loadable(lazy(() => import('pages/InstagramTemplates')));
const PlatformDefaults = Loadable(lazy(() => import('pages/PlatformDefaults')));

// New super-admin pages
const AuditLog = Loadable(lazy(() => import('pages/AuditLog')));
const PlatformNotifications = Loadable(lazy(() => import('pages/PlatformNotifications')));
const Settings = Loadable(lazy(() => import('pages/Settings')));
const Systemdefaults = Loadable(lazy(() => import('pages/SystemDefaults')));
const Promocodes = Loadable(lazy(() => import('pages/Promocodes')));
const FeatureFlags = Loadable(lazy(() => import('pages/FeatureFlags')));
const UsersWorkspaces = Loadable(lazy(() => import('pages/UsersWorkspaces')));
const Permissions = Loadable(lazy(() => import('pages/Permissions')));
const VendorModels = Loadable(lazy(() => import('pages/VendorModels')));
const AgentTemplates = Loadable(lazy(() => import('pages/AgentTemplates')));
const Prompts = Loadable(lazy(() => import('pages/Prompts')));
const WorkspaceAssistant = Loadable(lazy(() => import('pages/WorkspaceAssistant')));

// PCM — post-conversation analytics (see PCM_ANALYTICS_API.md)
const Pcm = Loadable(lazy(() => import('pages/PCM')));
const PcmWorkspace = Loadable(lazy(() => import('pages/PCM/workspace')));
const PcmDetail = Loadable(lazy(() => import('pages/PCM/detail')));

// Blog (public read-only content, see BLOG_API_FRONTEND_SPEC.md)
const Blog = Loadable(lazy(() => import('pages/Blog')));
const BlogPostDetail = Loadable(lazy(() => import('pages/Blog/PostDetail')));

// ── Hidden — page files exist on disk but rely on removed APIs. Re-enable
//    alongside a rebuild against /api/super/* (see api/_legacy.js).
//
// const DIDManagementCustomer    = Loadable(lazy(() => import('pages/DIDManagement/Customer')));
// const TemplateCustomer         = Loadable(lazy(() => import('pages/Template/Customer')));
// const VoiceCustomer            = Loadable(lazy(() => import('pages/Voice/Customer')));
// const ActionCustomer           = Loadable(lazy(() => import('pages/Action/Customer')));
// const LanguageCustomer         = Loadable(lazy(() => import('pages/Language/Customer')));
// const CallTypeCustomer         = Loadable(lazy(() => import('pages/CallType/Customer')));
// const Livekit                  = Loadable(lazy(() => import('pages/CredentialsManager/Livekit')));
// const StoragePage              = Loadable(lazy(() => import('pages/CredentialsManager/Storage')));
// const RolesAndPermissionsPublic   = Loadable(lazy(() => import('pages/RolesAndPermissions/Public')));
// const RolesAndPermissionsCustomer = Loadable(lazy(() => import('pages/RolesAndPermissions/Customer')));
// const UsersPublic        = Loadable(lazy(() => import('pages/Users/Public')));
// const UsersCustomer      = Loadable(lazy(() => import('pages/Users/Customer')));
// const FeatureCustomer    = Loadable(lazy(() => import('pages/Features/featureCustomer')));
// const Pricing            = Loadable(lazy(() => import('pages/Pricing')));
// const AgentCustomer      = Loadable(lazy(() => import('pages/Agents/agentCustomer')));
// const LLMVendorModels    = Loadable(lazy(() => import('pages/LLMVendorModels')));  // legacy CRUD page; replaced by pages/VendorModels

// ==============================|| MAIN ROUTING ||============================== //
const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        // Tenants
        { path: 'customer', element: <Customer /> },
        { path: 'customer/:schema', element: <TenantDetail /> },

        // Telephony
        { path: 'didManagement/public', element: <DIDManagementPublic /> },
        { path: 'sip-trunks', element: <SipTrunks /> },
        { path: 'inbound-dids', element: <InboundDIDs /> },
        { path: 'domains', element: <Domains /> },

        // Reference data
        { path: 'template/public', element: <TemplatePublic /> },
        { path: 'voice/public', element: <VoicePublic /> },
        { path: 'action/public', element: <ActionPublic /> },
        { path: 'language/public', element: <LanguagePublic /> },
        { path: 'callType/public', element: <CallTypePublic /> },
        { path: 'reference/channels', element: <ReferenceChannels /> },
        { path: 'reference/whatsapp-templates', element: <WhatsAppTemplates /> },
        { path: 'reference/instagram-templates', element: <InstagramTemplates /> },

        // New super-admin sections
        { path: 'platform-defaults', element: <PlatformDefaults /> },
        { path: 'audit-log', element: <AuditLog /> },
        { path: 'platform-alerts', element: <PlatformNotifications /> },
        { path: 'system-defaults', element: <Systemdefaults /> },
        { path: 'settings', element: <Settings /> },
        { path: 'promocodes', element: <Promocodes /> },
        { path: 'feature-flags', element: <FeatureFlags /> },
        { path: 'users-workspaces', element: <UsersWorkspaces /> },
        { path: 'permissions', element: <Permissions /> },
        { path: 'vendorModels', element: <VendorModels /> },
        { path: 'agent-templates', element: <AgentTemplates /> },
        { path: 'prompts', element: <Prompts /> },
        { path: 'workspace-assistant', element: <WorkspaceAssistant /> },

        // PCM
        { path: 'pcm', element: <Pcm /> },
        { path: 'pcm/:schema', element: <PcmWorkspace /> },
        { path: 'pcm/:schema/conversations/:conversationId', element: <PcmDetail /> },

        // Blog
        { path: 'blog', element: <Blog /> },
        { path: 'blog/:slug', element: <BlogPostDetail /> },

        { path: '404', element: <Page404 /> }
      ]
    },
    {
      path: '/auth',
      element: <CommonLayout />,
      children: [
        { path: 'login', element: <AuthLogin /> },
        { path: 'register', element: <AuthRegister /> },
        { path: 'forgot-password', element: <AuthForgotPassword /> },
        { path: 'reset-password', element: <AuthResetPassword /> },
        { path: 'check-mail', element: <AuthCheckMail /> },
        { path: 'code-verification', element: <AuthCodeVerification /> }
      ]
    }
  ]
};

export default MainRoutes;
